import {
  Card,
  Form,
  List,
  Typography,
  Tag,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Dropdown,
  theme,
} from 'antd';
import {
  ClockCircleOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  BuildOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ToolOutlined,
  PlusOutlined,
  EditOutlined,
  DownOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import React, { useState } from 'react';
import dayjs from 'libs/dayjs';
import { dateFormatter } from 'utils/formaters';
import { usePostApi, usePutApi } from 'hooks/useApi';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import useTenantFlags from 'hooks/useTenantFlags';
import { estadoDeVencimiento, hoyEnSantiago } from 'services/misTareas';

const { Text } = Typography;

// Opciones de estado para el dropdown
const statusOptions = [
  {
    key: '0',
    value: 0,
    label: 'Sin iniciar',
    icon: <ClockCircleOutlined />,
    color: 'default',
  },
  {
    key: '1',
    value: 1,
    label: 'Iniciado',
    icon: <PlayCircleOutlined />,
    color: 'processing',
  },
  {
    key: '3',
    value: 3,
    label: 'Finalizado',
    icon: <CheckCircleOutlined />,
    color: 'success',
  },
];

const itemStatus = {
  0: {
    color: 'volcano',
    text: 'Sin iniciar',
    icon: <ClockCircleOutlined />,
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-600',
  },
  1: {
    color: 'blue',
    text: 'Iniciado',
    icon: <PlayCircleOutlined />,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
  },
  3: {
    color: 'green',
    text: 'Finalizado',
    icon: <CheckCircleOutlined />,
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-600',
  },
};

const OrderItemList = (props) => {
  const { items, isLoading, orderId, onItemUpdate } = props;

  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [itemStatusChanges, setItemStatusChanges] = useState({});
  const [form] = Form.useForm();
  const { readOnly } = useTenantFlags();
  const { token } = theme.useToken();
  const hoy = hoyEnSantiago();

  // Color del tag de fecha planificada segun su vencimiento respecto a hoy.
  const colorFechaPlanificada = (estado) => {
    if (estado === 'vencida') return token.colorError;
    if (estado === 'estaSemana') return token.colorWarning;
    return token.colorTextSecondary;
  };

  // Hooks para APIs
  const [createItem] = usePostApi({ url: 'order/item', successEnabled: true });
  const [updateItem] = usePutApi({ url: 'order/item', successEnabled: true });
  const [updateItemStatus] = usePutApi({
    url: 'order/items/status',
    successEnabled: true,
  });

  // Filtrar items por estado (excluyendo ocultos)
  const filteredItems =
    items?.filter((item) => {
      // Excluir items ocultos (status_id = 10)
      if (item.status_id === 10) {
        return false;
      }

      // Aplicar filtro por estado
      if (statusFilter === 'all') {
        return true;
      }

      return item.item_status_id === parseInt(statusFilter);
    }) || [];

  // Función para iniciar la creación de un nuevo item
  const handleCreateItem = () => {
    setIsCreating(true);
    setEditingItem({
      id_item: null,
      description: '',
      elevator: '',
      item_status_id: 0,
      isNew: true,
    });
    form.setFieldsValue({
      description: '',
      elevator: '',
    });
  };

  // Función para iniciar la edición de un item existente
  const handleEditItem = (item) => {
    setEditingItem({ ...item, isNew: false });
    setIsEditing(true);
    form.setFieldsValue({
      description: item.description,
      elevator: item.elevator,
    });
  };

  // Función para cancelar la edición/creación
  const handleCancelEdit = () => {
    setEditingItem(null);
    setIsCreating(false);
    setIsEditing(false);
    form.resetFields();
  };

  // Función para cambiar el estado de un item individual
  const handleStatusChange = (itemId, newStatus) => {
    setItemStatusChanges((prev) => ({
      ...prev,
      [itemId]: newStatus,
    }));
  };

  // Función para guardar todos los cambios pendientes
  const handleSaveAllChanges = async () => {
    const pendingChanges = Object.keys(itemStatusChanges);
    if (pendingChanges.length === 0) return;

    try {
      // Agrupar cambios por estado
      const changesByStatus = {};
      pendingChanges.forEach((itemId) => {
        const newStatus = itemStatusChanges[itemId];
        if (!changesByStatus[newStatus]) {
          changesByStatus[newStatus] = [];
        }
        changesByStatus[newStatus].push(itemId);
      });

      // Enviar cada grupo de cambios
      const promises = Object.entries(changesByStatus).map(([status, items]) =>
        updateItemStatus({
          items: items,
          newStatus: parseInt(status),
        })
      );

      await Promise.all(promises);
      // Limpiar todos los cambios pendientes
      setItemStatusChanges({});
      onItemUpdate && onItemUpdate();
    } catch (error) {
      // noop: errores ya se notifican desde los hooks usePutApi/usePostApi
    }
  };

  // Función para cancelar todos los cambios pendientes
  const handleCancelAllChanges = () => {
    setItemStatusChanges({});
  };

  // Función para guardar los cambios
  const handleSaveItem = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem.isNew) {
        // Crear nuevo item
        const result = await createItem({
          order_id: orderId,
          description: values.description,
          elevator: values.elevator,
          item_status_id: 0,
        });

        if (result.state === 'success') {
          setIsCreating(false);
          onItemUpdate && onItemUpdate();
        }
      } else {
        // Actualizar item existente
        const result = await updateItem({
          id_item: editingItem.id_item,
          description: values.description,
          elevator: values.elevator,
        });

        if (result.state === 'success') {
          setIsEditing(false);
          onItemUpdate && onItemUpdate();
        }
      }

      handleCancelEdit();
    } catch (error) {
      // noop: errores ya se notifican desde los hooks usePutApi/usePostApi
    }
  };

  // Verificar si realmente no hay items (solo cuando no hay items en absoluto)
  const hasNoItemsAtAll = !items || items.length === 0;

  return (
    <>
      {/* Filtro por estado */}
      <div className="mb-6">
        <Card className="shadow-sm border-0">
          <div className="flex items-center justify-between flex-col md:flex-row">
            <div className="flex items-center space-x-3">
              <Text strong className="text-gray-700">
                Filtrar por estado:
              </Text>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                className="w-48"
                placeholder="Seleccionar estado"
              >
                <Select.Option value="all">
                  <Space>
                    <ToolOutlined />
                    <span>Todos los estados</span>
                  </Space>
                </Select.Option>
                <Select.Option value="0">
                  <Space>
                    <ClockCircleOutlined />
                    <span>Sin iniciar</span>
                  </Space>
                </Select.Option>
                <Select.Option value="1">
                  <Space>
                    <PlayCircleOutlined />
                    <span>Iniciado</span>
                  </Space>
                </Select.Option>
                <Select.Option value="3">
                  <Space>
                    <CheckCircleOutlined />
                    <span>Finalizado</span>
                  </Space>
                </Select.Option>
              </Select>
            </div>
            <div className="flex items-center space-x-4 flex-col md:flex-row">
              <div className="text-sm text-gray-500">
                Mostrando {filteredItems.length} de{' '}
                {items?.filter((item) => item.status_id !== 10).length || 0} items
              </div>

              {/* Indicador de cambios pendientes y botón de guardar */}
              {!readOnly && Object.keys(itemStatusChanges).length > 0 && (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                    <Text className="text-sm text-orange-600 font-medium">
                      {Object.keys(itemStatusChanges).length} cambio(s) sin guardar
                    </Text>
                  </div>
                  <Button
                    type="default"
                    onClick={handleCancelAllChanges}
                    size="small"
                    className="border-gray-300 text-gray-600 hover:border-gray-400"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveAllChanges}
                    className="bg-green-600 hover:bg-green-700 border-green-600"
                    size="small"
                  >
                    Guardar Cambios
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Mostrar mensaje cuando no hay items filtrados pero sí hay items en total */}
      {filteredItems.length === 0 && !hasNoItemsAtAll && (
        <div className="text-center py-12 mb-6">
          <ToolOutlined className="text-6xl text-gray-300 mb-4" />
          <Text className="text-lg text-gray-500 mb-2 block">No hay items con este estado</Text>
          <Text type="secondary">Intenta cambiar el filtro de estado para ver otros items</Text>
        </div>
      )}

      {/* Mostrar mensaje cuando realmente no hay items */}
      {hasNoItemsAtAll && (
        <div className="text-center py-12 mb-6">
          <ToolOutlined className="text-6xl text-gray-300 mb-4" />
          <Text className="text-lg text-gray-500 mb-2 block">No hay items disponibles</Text>
          <Text type="secondary">No se encontraron items para esta orden</Text>
          {!readOnly && (
            <div className="mt-6">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateItem}
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Agregar Primer Item
              </Button>
            </div>
          )}
        </div>
      )}

      <List
        loading={isLoading}
        dataSource={filteredItems}
        grid={{
          gutter: [24, 24],
          xs: 1,
          sm: 1,
          md: 1,
          lg: 2,
          xl: 2,
          xxl: 3,
        }}
        renderItem={(item) => {
          const status = itemStatus[item.item_status_id] || itemStatus[0];
          const estadoFecha = item.planned_end ? estadoDeVencimiento(item, hoy) : null;

          return (
            <List.Item key={item.id_item}>
              <Card
                className={`shadow-sm border-0 hover:shadow-lg transition-all duration-300 ${status.bgColor} ${status.borderColor}`}
                hoverable
                styles={{ body: { padding: '20px' } }}
              >
                {/* Header con estado */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div className={`p-2 rounded-lg ${status.bgColor}`}>
                      <ToolOutlined className={`text-lg ${status.textColor}`} />
                    </div>
                    <div>
                      <Text strong className={`text-lg ${status.textColor}`}>
                        Item #{item.id_item}
                      </Text>
                      <div className="flex items-center space-x-1 mt-1">
                        {status.icon}
                        <Text className={`text-sm ${status.textColor}`}>{status.text}</Text>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-wrap">
                    <Tag color={status.color} className="px-3 py-1 rounded-full font-medium">
                      {status.text}
                    </Tag>
                    {estadoFecha && (
                      <Tag
                        icon={<CalendarOutlined />}
                        style={{
                          color: colorFechaPlanificada(estadoFecha),
                          borderColor: colorFechaPlanificada(estadoFecha),
                        }}
                        className="px-2 rounded-full text-xs"
                      >
                        {dayjs(item.planned_end).format('DD/MM')}
                      </Tag>
                    )}
                    {!readOnly && (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditItem(item)}
                        className="text-gray-500 hover:text-blue-600"
                        title="Editar item"
                      />
                    )}
                  </div>
                </div>

                {/* Información del item */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-start space-x-3">
                    <FileTextOutlined className="text-gray-400 mt-1" />
                    <div className="flex-1">
                      <Text type="secondary" className="text-sm">
                        Descripción
                      </Text>
                      <div>
                        <Text className="text-gray-700 whitespace-pre-wrap break-words">
                          {item.description}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <BuildOutlined className="text-gray-400 mt-1" />
                    <div>
                      <Text type="secondary" className="text-sm">
                        Ascensores
                      </Text>
                      <div>
                        <Text className="text-gray-700">{item.elevator || 'No especificado'}</Text>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CalendarOutlined className="text-gray-400 mt-1" />
                    <div>
                      <Text type="secondary" className="text-sm">
                        Última Actualización
                      </Text>
                      <div>
                        <Text className="text-gray-700">
                          {item.modified_date_item
                            ? dateFormatter(item.modified_date_item).short_h
                            : 'Sin iniciar'}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <ToolOutlined className="text-gray-400 mt-1" />
                    <div className="flex-1 min-w-0">
                      <Text type="secondary" className="text-sm">
                        Hallazgo vinculado
                      </Text>
                      <div className="mt-1">
                        {item.Hallazgo ? (
                          <HallazgoChip hallazgo={item.Hallazgo} compact />
                        ) : (
                          <Tag color="default">Tarea genérica</Tag>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cambio de estado individual */}
                {!readOnly && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Text className="text-sm font-medium text-gray-600">Cambiar estado:</Text>
                        <Dropdown
                          menu={{
                            items: statusOptions.map((option) => ({
                              key: option.key,
                              label: (
                                <Space>
                                  {option.icon}
                                  <span>{option.label}</span>
                                </Space>
                              ),
                              onClick: () => handleStatusChange(item.id_item, option.value),
                            })),
                          }}
                          trigger={['click']}
                        >
                          <Button className="flex items-center space-x-2">
                            <span>
                              {statusOptions.find(
                                (opt) =>
                                  opt.value ===
                                  (itemStatusChanges[item.id_item] || item.item_status_id)
                              )?.label || 'Seleccionar estado'}
                            </span>
                            <DownOutlined />
                          </Button>
                        </Dropdown>
                      </div>

                      {/* Indicador de cambios pendientes */}
                      {itemStatusChanges[item.id_item] && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <Text className="text-xs text-orange-600 font-medium">Sin guardar</Text>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </List.Item>
          );
        }}
      />

      {/* Botón para agregar nuevo item - siempre visible */}
      {!readOnly && !hasNoItemsAtAll && (
        <div className="mt-6 text-center">
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleCreateItem}
            className="w-full h-16 rounded-lg border-2 border-dashed border-blue-300 hover:border-blue-500 text-blue-600 hover:text-blue-700"
            disabled={isCreating}
          >
            <span className="text-lg font-medium">Agregar Nuevo Item</span>
          </Button>
        </div>
      )}

      {/* Modal para crear nuevo item */}
      <Modal
        title={
          <span className="flex items-center space-x-2">
            <PlusOutlined className="text-blue-600" />
            <span>Crear Nuevo Item</span>
          </span>
        }
        open={isCreating}
        onCancel={handleCancelEdit}
        footer={[
          <Button key="cancel" onClick={handleCancelEdit}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveItem}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          >
            Crear Item
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="description"
            label={
              <span className="flex items-center space-x-1">
                <FileTextOutlined className="text-gray-400" />
                <span className="font-medium">Descripción del Trabajo</span>
              </span>
            }
            rules={[
              { required: true, message: 'La descripción es obligatoria' },
              {
                min: 10,
                message: 'La descripción debe tener al menos 10 caracteres',
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Describe detalladamente el trabajo a realizar..."
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="elevator"
            label={
              <span className="flex items-center space-x-1">
                <BuildOutlined className="text-gray-400" />
                <span className="font-medium">Ascensores</span>
              </span>
            }
          >
            <Input
              placeholder="Ej: Ascensor 1, Ascensor 2 (separados por comas)"
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para editar item existente */}
      <Modal
        title={
          <span className="flex items-center space-x-2">
            <EditOutlined className="text-blue-600" />
            <span>Editar Item #{editingItem?.id_item}</span>
          </span>
        }
        open={isEditing}
        onCancel={handleCancelEdit}
        footer={[
          <Button key="cancel" onClick={handleCancelEdit}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveItem}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          >
            Guardar Cambios
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="description"
            label={
              <span className="flex items-center space-x-1">
                <FileTextOutlined className="text-gray-400" />
                <span className="font-medium">Descripción del Trabajo</span>
              </span>
            }
            rules={[
              { required: true, message: 'La descripción es obligatoria' },
              {
                min: 10,
                message: 'La descripción debe tener al menos 10 caracteres',
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Describe detalladamente el trabajo a realizar..."
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="elevator"
            label={
              <span className="flex items-center space-x-1">
                <BuildOutlined className="text-gray-400" />
                <span className="font-medium">Ascensores</span>
              </span>
            }
          >
            <Input
              placeholder="Ej: Ascensor 1, Ascensor 2 (separados por comas)"
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default OrderItemList;
